import { BackgroundColorsMap, GradientsColorsMap, MilanBackgroundColors, Typography } from '@milanlaser/mlcomponents';
import { Metro } from 'clinics/clinics';
import { type PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import React, { useMemo, useEffect, useRef } from 'react';
import { useReviewsIoScript } from '../../../../hooks/useReviewsIoScript';
import { initializeReviewTabSwitcher } from '../../../../utils/autoTabSwitcher';
import { ReviewsData } from '../../../../utils/ssr/types';

declare global {
    interface Window {
        ReviewsWidget: {
            new (id: string, config: unknown): unknown;
        };
    }
}

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state: BuilderState<T>;
    };
};

type S37ReviewsCityOverallProps = BuilderProps<{
    metro: Metro;
    reviewsData?: ReviewsData;
}> & {
    title: string;
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    backgroundColor?: MilanBackgroundColors;
    backgroundGradient?: MilanBackgroundColors;
};

export function S37ReviewsCityOverall(props: S37ReviewsCityOverallProps) {
    // Don't render at all if env variable is not set to true.
    // Must run before any hooks to satisfy the Rules of Hooks.
    if (!process.env.GATSBY_SHOW_REVIEWS_IO || process.env.GATSBY_SHOW_REVIEWS_IO !== 'true') {
        return null;
    }

    const {
        title,
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        backgroundColor,
        backgroundGradient,
        ...restProps
    } = props;

    const {
        builderState: {
            state: { metro, reviewsData },
        },
    } = restProps;

    const metroSku = metro?.stores?.map((s) => s.salesforceValue).join(';') ?? '';
    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;
    const backgroundStyle = (backgroundColor && BackgroundColorsMap[backgroundColor]) || 'None';
    const backgroundGradientStyle =
        backgroundGradient && backgroundGradient in GradientsColorsMap
            ? GradientsColorsMap[backgroundGradient as keyof typeof GradientsColorsMap]
            : 'None';

    const reviews = reviewsData
        ? {
              value: reviewsData.average || 0,
              reviewCount: reviewsData.total || 0,
          }
        : undefined;

    const renderReviews = reviews !== undefined && reviews.reviewCount > 0;
    const tabSwitcherCleanupRef = useRef<(() => void) | null>(null);
    const { isLoaded: scriptLoaded, isError: scriptError } = useReviewsIoScript();
    const widgetInitialized = useRef(false);

    const reviewsConfig = useMemo(() => {
        return {
            store: 'milanlaser.com',
            widget: 'polaris',

            options: {
                types: 'store_review,product_review,third_party_review',
                enable_sentiment_analysis: true,
                lang: 'en',
                layout: '',
                per_page: 3,
                store_review: {
                    hide_if_no_results: false,
                },
                third_party_review: {
                    hide_if_no_results: false,
                },
                product_review: {
                    sku: metroSku,
                    hide_if_no_results: false,
                },
                questions: {
                    hide_if_no_results: false,
                    enable_ask_question: true,
                    enable_ask_question_button_style: false,
                    show_dates: true,
                    grouping: '[Group questions by providing a grouping variable here or a specific product SKU]',
                },
                header: {
                    enable_summary: true,
                    enable_ratings: true,
                    enable_attributes: false,
                    enable_image_gallery: true,
                    enable_percent_recommended: false,
                    enable_write_review: false,
                    enable_ask_question: false,
                    enable_sub_header: true,
                    rating_decimal_places: 2,
                    use_write_review_button: false,
                    enable_if_no_results: false,
                },

                sentiment: {
                    badge_text: 'Our Customers Say',
                },

                filtering: {
                    enable: true,
                    enable_text_search: false,
                    enable_sorting: true,
                    enable_product_filter: false,
                    enable_media_filter: true,
                    enable_overall_rating_filter: true,
                    enable_language_filter: false,
                    enable_language_filter_language_change: false,
                    enable_ratings_filters: true,
                    enable_attributes_filters: true,
                    enable_expanded_filters: false,
                },

                reviews: {
                    enable_avatar: true,
                    enable_reviewer_name: true,
                    enable_reviewer_address: true,
                    reviewer_address_format: 'city, country',
                    enable_verified_badge: true,
                    enable_subscriber_badge: false,
                    review_content_filter: 'undefined',
                    enable_reviewer_recommends: true,
                    enable_attributes: true,
                    enable_product_name: true,
                    enable_review_title: true,
                    enable_replies: true,
                    enable_images: true,
                    enable_ratings: true,
                    enable_share: true,
                    enable_helpful_vote: true,
                    enable_helpful_display: true,
                    enable_report: true,
                    enable_date: true,
                    enable_third_party_source: true,
                    enable_duplicate_reviews: undefined,
                },
            },
            translations: {
                'Verified Customer': 'Verified Customer',
            },
            styles: {
                '--base-font-size': '16px',

                '--common-button-font-family': 'proxima-nova, sans-serif',
                '--common-button-font-size': '18px',
                '--common-button-font-weight': '500',
                '--common-button-letter-spacing': '0',
                '--common-button-text-transform': 'none',
                '--common-button-vertical-padding': '14px',
                '--common-button-horizontal-padding': '20px',
                '--common-button-border-width': '2px',
                '--common-button-border-radius': '30px',

                '--primary-button-bg-color': '#0088AB',
                '--primary-button-border-color': '#FFFFFF00',
                '--primary-button-text-color': '#ffffff',

                '--secondary-button-bg-color': '#003542',
                '--secondary-button-border-color': '#FFFFFF00',
                '--secondary-button-text-color': '#FFFFFF',

                '--common-star-color': '#F4D500',
                '--common-star-disabled-color': 'rgba(0,0,0,0.25)',
                '--medium-star-size': '22px',
                '--small-star-size': '19px',

                '--heading-text-color': '#00A5B4',
                '--heading-text-font-weight': '600',
                '--heading-text-font-family': 'greycliff-cf, sans-serif',
                '--heading-text-line-height': '1.4',
                '--heading-text-letter-spacing': '0',
                '--heading-text-transform': 'none',

                '--body-text-color': '#003542',
                '--body-text-font-weight': '400',
                '--body-text-font-family': 'proxima-nova, sans-serif',
                '--body-text-line-height': '1.4',
                '--body-text-letter-spacing': '0',
                '--body-text-transform': 'none',

                '--inputfield-text-font-family': 'proxima-nova, sans-serif',
                '--input-text-font-size': '18px',
                '--inputfield-text-font-weight': '400',
                '--inputfield-text-color': '#003542',
                '--inputfield-border-color': 'rgba(0,0,0,0.2)',
                '--inputfield-background-color': '#FFFFFF',
                '--inputfield-border-width': '1px',
                '--inputfield-border-radius': '30px',

                '--common-border-color': '#90D5D5',
                '--common-border-width': '1px',
                '--common-sidebar-width': '190px',

                '--filters-panel-bg-color': 'transparent',
                '--filters-panel-font-size': '16px',
                '--filters-panel-text-color': '#003542',
                '--filters-panel-horizontal-padding': '0',
                '--filters-panel-vertical-padding': '0',

                '--slider-indicator-bg-color': 'rgba(0,0,0,0.1)',
                '--slider-indicator-button-color': '#0E1311',
                '--slider-indicator-width': '190px',

                '--badge-icon-color': '#0E1311',
                '--badge-icon-font-size': 'inherit',
                '--badge-text-color': '#0E1311',
                '--badge-text-font-size': 'inherit',
                '--badge-text-letter-spacing': 'inherit',
                '--badge-text-transform': 'inherit',

                '--author-font-size': 'inherit',
                '--author-text-transform': 'none',

                '--avatar-thumbnail-size': '60px',
                '--avatar-thumbnail-border-radius': '30px',
                '--avatar-thumbnail-text-color': '#FFFFFF',
                '--avatar-thumbnail-bg-color': '#0088AB',

                '--photo-video-thumbnail-size': '80px',
                '--photo-video-thumbnail-border-radius': '30px',

                '--mediaslider-scroll-button-icon-color': '#0088AB',
                '--mediaslider-scroll-button-bg-color': '#FFFFFFD9',
                '--mediaslider-overlay-text-color': '#FFFFFF',
                '--mediaslider-overlay-bg-color': '#003542B8',
                '--mediaslider-item-size': '92px',

                '--pagination-tab-text-color': '#003542',
                '--pagination-tab-text-transform': 'none',
                '--pagination-tab-text-letter-spacing': '0',
                '--pagination-tab-text-font-size': '18px',
                '--pagination-tab-text-font-weight': '400',

                '--pagination-tab-active-text-color': '#00A5B4',
                '--pagination-tab-active-text-font-weight': '600',
                '--pagination-tab-active-border-color': '#00A5B4',
                '--pagination-tab-border-width': '1px',

                '--sentiment-base-font-size': '15px',
                '--sentiment-panel-bg-color': 'transparent',
                '--sentiment-panel-border-size': '1px',
                '--sentiment-panel-border-color': 'rgba(0, 0, 0, 0.1)',
                '--sentiment-panel-border-radius': '0px',
                '--sentiment-panel-shadow-size': '0px',
                '--sentiment-panel-shadow-color': 'rgba(0, 0, 0, 0.1)',
                '--sentiment-heading-text-color': '#0E1311',
                '--sentiment-heading-text-font-weight': '600',
                '--sentiment-heading-text-font-family': 'inherit',
                '--sentiment-heading-text-line-height': '1.4',
                '--sentiment-heading-text-letter-spacing': '0',
                '--sentiment-heading-text-transform': 'none',
                '--sentiment-body-text-color': '#0E1311',
                '--sentiment-body-text-font-weight': '400',
                '--sentiment-body-text-font-family': 'inherit',
                '--sentiment-body-text-line-height': '1.4',
                '--sentiment-body-text-letter-spacing': '0',
                '--sentiment-body-text-transform': 'none',
                '--sentiment-panel-vertical-padding': '25px',
                '--sentiment-panel-horizontal-padding': '20px',
                '--sentiment-header-text-color': 'inherit',
                '--sentiment-header-text-font-size': '12px',
                '--sentiment-header-text-font-weight': 'inherit',
                '--sentiment-header-bg-color': 'rgba(0, 0, 0, 0.04)',
                '--sentiment-header-border-radius': '50px',
                '--sentiment-header-shadow-size': '0px',
                '--sentiment-header-shadow-color': 'rgba(0, 0, 0, 0.1)',
                '--sentiment-header-vertical-padding': '7px',
                '--sentiment-header-horizontal-padding': '10px',
                '--sentiment-pagination-tab-text-font-size': '13px',
                '--sentiment-pagination-tab-text-font-weight': '600',
                '--sentiment-pagination-tab-text-color': '#0E1311',
                '--sentiment-pagination-tab-text-transform': 'none',
                '--sentiment-pagination-tab-text-letter-spacing': '0',
                '--sentiment-pagination-tab-active-text-color': '#0E1311',
                '--sentiment-pagination-tab-active-text-font-weight': '600',
                '--sentiment-pagination-tab-active-border-color': '#0E1311',
                '--sentiment-pagination-tab-border-width': '3px',
            },
        };
    }, [metroSku]);

    useEffect(() => {
        // Skip if we shouldn't render or script isn't loaded yet or already initialized
        if (!renderReviews || !scriptLoaded || widgetInitialized.current) {
            return;
        }

        // Handle script error
        if (scriptError) {
            console.error('Failed to load Reviews.io script');
            return;
        }

        try {
            // Initialize the Reviews.io widget
            new window.ReviewsWidget('#reviews', reviewsConfig);
            widgetInitialized.current = true;

            // Initialize the tab switcher after widget creation
            // This will automatically switch to Product Review tab when it appears
            tabSwitcherCleanupRef.current = initializeReviewTabSwitcher({
                // Now uses defaults: maxRetries: 10, retryDelay: 200
                // Can override if needed:
                // maxRetries: 20,
                // retryDelay: 150,
                debug: false, // Set to true for debugging
            });
        } catch (error) {
            console.error('Error initializing ReviewsWidget:', error);
            widgetInitialized.current = false;
        }

        // Cleanup function
        return () => {
            // Clean up tab switcher
            if (tabSwitcherCleanupRef.current) {
                tabSwitcherCleanupRef.current();
                tabSwitcherCleanupRef.current = null;
            }
            widgetInitialized.current = false;
        };
    }, [renderReviews, scriptLoaded, scriptError, reviewsConfig]);

    // Only show the city overview widget for multi-store metros
    if (!metro.stores || metro.stores.length <= 1) {
        return null;
    }

    if (!renderReviews || scriptError) {
        return null;
    }

    // Show a loading state while script is loading
    if (!scriptLoaded) {
        return (
            <section
                className={`mcl-mx-auto mcl-px-4 md:mcl-px-6 xl:mcl-px-4 mcl-w-full ${backgroundStyle} ${backgroundGradientStyle} ${topPaddingClasses} ${bottomPaddingClasses}`}
            >
                <div className="container">
                    <div className="mcl-text-center mcl-py-8">
                        <span className="mcl-text-gray-500">Loading reviews...</span>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            id="reviews-section"
            className={`mcl-mx-auto mcl-px-4 md:mcl-px-6 xl:mcl-px-4 mcl-w-full ${backgroundStyle} ${backgroundGradientStyle} ${topPaddingClasses} ${bottomPaddingClasses}`}
        >
            <Typography className="mcl-text-center mcl-mt-0 mcl-mb-12" color="Blue" tag="h2" variant="h2-large">
                {title}
            </Typography>
            <div className="container">
                <div id="reviews" />
            </div>
        </section>
    );
}
